import {
  reduceIncomingPrompt,
  transformIncomingPrompt,
} from '@/utils/dataTransformers'
import { defineStore } from 'pinia'
import { useAudioFileStore } from '@/stores/audioFile/store'
import { useNetworkStore } from '@/stores/network/store'
import * as Api from './api'
import {
  callWithMeta,
  createMeta,
  getMetaInitial,
  waitFor,
  type Meta,
} from '@/utils/store'
import * as ApiAudioFile from '@/stores/audioFile/api'
import type { ColorName } from '@/utils/colors'

function getUniqueId(prompt: ApiAudioFile.AudioFile) {
  return `${prompt.category}/${prompt.id}`
}

type State = {
  prompts: ApiAudioFile.AudioFile[]
  assignedMusicOnHold: number | string
  meta: Meta<{
    prompts: undefined
    load: undefined
    changeColor: undefined
    delete: undefined
  }>
}

const getDefaultState = (): State => ({
  prompts: [],
  assignedMusicOnHold: '',
  meta: createMeta(['prompts', 'load', 'changeColor', 'delete']),
})

export const usePromptsStore = defineStore('prompts', {
  state: getDefaultState,

  getters: {
    find(): (id: string) => ApiAudioFile.AudioFile {
      return (id: string) => this.byId[id]
    },
    list(): ApiAudioFile.AudioFile[] {
      return this.allIds.map((id) => this.find(id))
    },
    audioFilesByCategory: (state) => (categoryName: string) =>
      state.prompts.filter((p) => p.category === categoryName),
    byId: (state) =>
      state.prompts.reduce(
        (byId, prompt) => {
          byId[getUniqueId(prompt)] = prompt

          return byId
        },
        {} as Record<string, ApiAudioFile.AudioFile>
      ),
    allIds: (state) =>
      Array.from(new Set(state.prompts.map((p) => getUniqueId(p)))),
  },

  actions: {
    RESET() {
      this.prompts = []
      this.meta.prompts = getMetaInitial()
    },

    SET_MUSIC_ON_HOLD(audioFileId: number | string) {
      this.assignedMusicOnHold = audioFileId
    },

    async load({ useCache = true } = {}) {
      const hasCached = ['pending', 'success'].includes(
        this.meta.prompts.status
      )

      if (useCache && hasCached) {
        await waitFor(() => this.meta.prompts.status !== 'pending')

        return
      }

      await callWithMeta({
        store: this,
        metaFields: { prompts: undefined, load: undefined },
        apiCall: async () => await Api.loadPrompts(),
        onSuccess: ({ result, isLatest }) => {
          if (isLatest('prompts')) {
            const prompts = result.payload?.length ? result.payload : []

            this.prompts = prompts.map((item: any) =>
              reduceIncomingPrompt(transformIncomingPrompt(item))
            )

            const assignedMusicOnHold = prompts.find(
              (audio: any) => audio.assigned && audio.type === 'music_on_hold'
            )?.id

            this.SET_MUSIC_ON_HOLD(assignedMusicOnHold)
          }
        },
        onError: ({ result, isLatest }) => {
          if (isLatest('load')) {
            useNetworkStore().HANDLE_LOADING_ERROR(result.payload)
          }
        },
      })
    },

    async changeColor({
      color,
      itemId,
      itemType,
    }: {
      color: ColorName | null
      itemId: number
      itemType: 'waiting_music' | 'voice_mail' | 'message' | 'music_on_hold'
    }) {
      // Show color change before waiting for API call

      const promptIndex = this.prompts.findIndex(
        (prompt) => prompt.id === itemId
      )

      if (promptIndex >= 0) {
        this.prompts[promptIndex].color = color
      }

      // Send color change to the BE

      const data = new FormData()

      const newColor = color ?? ''

      data.append('color', newColor)

      const { result } = await callWithMeta({
        store: this,
        metaFields: { changeColor: undefined },
        apiCall: async () =>
          await ApiAudioFile.updatePrompt(itemType, itemId, data),
        onSuccess: ({ result, isLatest }) => {
          if (isLatest('changeColor')) {
            const updatedPrompt = result.payload

            this.prompts = this.prompts.filter(
              (p) => getUniqueId(p) !== getUniqueId(updatedPrompt)
            )

            this.prompts.push(updatedPrompt)
          }
        },
      })

      if (result.status === 'error') {
        throw result.payload
      }
    },

    async delete(audioFileIds: number[]) {
      const { result } = await callWithMeta({
        store: this,
        metaFields: { prompts: undefined, delete: undefined },
        apiCall: async () => await Api.deleteAudioFiles(audioFileIds),
        onSuccess: ({ result, isLatest }) => {
          if (isLatest('prompts')) {
            this.prompts = this.prompts.filter(
              (p) => !result.payload.updated.includes(p.id)
            )

            if (
              result.payload.updated.includes(
                Number(useAudioFileStore().meta.audioFile.params?.id)
              )
            ) {
              useAudioFileStore().RESET()
            }
          }
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },
  },
})
